import { useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import { myToast } from "../../../libs";
import config from "../../../config";
import { apiCreateFile, apiIngestPropertyFile } from "../../../graphQL/file";

const IngestFileDialog = ({ open, onClose, domain, authorId, uploadSingle }) => {
  const [sourceName, setSourceName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const reset = () => {
    setSourceName("");
    setFile(null);
    setBusy(false);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const handleFileSelect = (e) => {
    const picked = e.target.files?.[0] || null;
    if (picked) setFile(picked);
  };

  const handleConfirm = () => {
    const name = String(sourceName || "").trim();
    if (!name || !file || !uploadSingle) return;
    if (!authorId) {
      myToast.error("Missing author context. Please sign in again.");
      return;
    }
    const domainKey = String(domain || "").trim();
    if (!domainKey) {
      myToast.error("Missing domain context.");
      return;
    }

    setBusy(true);
    setProgress(0);

    const key = `${domainKey}/ingest/${Date.now()}-${file.name}`;
    const url = `${config.ENV.REACT_APP_HLS_BASE_URL}/${key}`;

    uploadSingle(
      `${domainKey}/ingest`,
      file,
      file.name,
      (uploadProgress) => setProgress(uploadProgress),
      async (err, result, callbackDone) => {
        if (err) {
          setBusy(false);
          setProgress(0);
          myToast.error(err?.message || err?.graphQLErrors?.[0]?.message || "Upload failed");
          return;
        }
        try {
          const fileDoc = await apiCreateFile({
            authorId,
            fileCategory: "ingest",
            type: "PropertyImport",
            mimeType: file.type || "application/octet-stream",
            key: result?.key || key,
            url,
            size: String(file.size),
            description: name,
            domain: domainKey,
            status: "active",
            isPublic: false,
            isSystem: true,
            data: { ingest: { sourceName: name, status: "pending", target: "propertyAssets" } },
          });

          try {
            await apiIngestPropertyFile({ fileId: fileDoc.id, sourceName: name });
            myToast.success(`Ingest queued: ${name}`);
          } catch (queueErr) {
            myToast.error(
              queueErr?.message || queueErr?.graphQLErrors?.[0]?.message || "Failed to queue ingest"
            );
          }

          reset();
          onClose?.();
        } catch (saveErr) {
          setBusy(false);
          setProgress(0);
          myToast.error(
            saveErr?.message || saveErr?.graphQLErrors?.[0]?.message || "Upload failed to save"
          );
        }
        callbackDone && callbackDone();
      },
      { key, isPublic: false }
    );
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          m: 0,
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 24px)", sm: 560 },
          maxWidth: "560px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>Ingest File</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <TextField
          fullWidth
          size="small"
          label="Source name"
          required
          value={sourceName}
          onChange={(e) => setSourceName(e.target.value)}
          disabled={busy}
          sx={{ mt: 0.5 }}
        />

        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <input
            ref={fileInputRef}
            id="ingest-file-dialog-input"
            type="file"
            hidden
            accept=".xlsx,.csv"
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            Choose File
          </Button>
          {file ? (
            <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{file.name}</Typography>
          ) : null}
        </Box>

        {busy ? <LinearProgress variant="determinate" value={progress} sx={{ mt: 1.5 }} /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={busy || !String(sourceName || "").trim() || !file}
        >
          Ingest
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IngestFileDialog;
